// ============================================
// Enums
// ============================================

export enum Role {
  ADMIN = 'ADMIN',
  MANAGER = 'MANAGER',
  TEAM_MEMBER = 'TEAM_MEMBER'
}

export enum TaskStatus {
  TODO = 'TODO',
  IN_PROGRESS = 'IN_PROGRESS',
  DONE = 'DONE'
}

export enum ProjectStatus {
  ACTIVE = 'active',
  ARCHIVED = 'archived',
  COMPLETED = 'completed'
}

// ============================================
// Core Entities
// ============================================

export interface User {
  id: string;
  name: string;
  email: string;
  role: Role;
  avatar: string;
}

export interface ChecklistItem {
  id: string;
  text: string;
  completed: boolean;
}

export interface Task {
  id: string;
  projectId: string;
  title: string;
  description: string;
  status: TaskStatus;
  assigneeId: string;
  deadline: string;
  estimateHours: number;
  priority: TaskPriority;
  checklist?: ChecklistItem[];
}

export type TaskPriority = 'Low' | 'Medium' | 'High';

export interface Project {
  id: string;
  name: string;
  description: string;
  ownerId: string;
  memberIds: string[];
  createdAt: string;
  deadline: string;
  status: ProjectStatus | 'active' | 'archived' | 'completed';
}

export interface ChatMessage {
  id: string;
  projectId: string;
  senderId: string;
  content: string;
  timestamp: number;
}

export interface Notification {
  id: string;
  userId: string;
  title: string;
  message: string;
  isRead: boolean;
  timestamp: number;
  link?: string;
}

export type TimelineEventType = 
  | 'task_created' 
  | 'task_deleted' 
  | 'task_updated' 
  | 'task_status_changed' 
  | 'member_added' 
  | 'member_removed' 
  | 'project_updated' 
  | 'project_created';

export interface TimelineEvent {
  id: string;
  projectId: string;
  userId: string;
  userName?: string;
  type: TimelineEventType;
  details: string;
  timestamp: number;
}

// ============================================
// API Response Types
// ============================================

export interface ApiResponse<T> {
  data: T | null;
  error: string | null;
  success: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

// ============================================
// Form Types
// ============================================

export interface TaskFormData {
  title: string;
  description: string;
  assigneeId: string;
  deadline: string;
  priority: TaskPriority;
  estimateHours?: number;
}

export interface ProjectFormData {
  name: string;
  description: string;
  deadline: string;
  memberIds: string[];
}

// ============================================
// UI State Types
// ============================================

export interface FeedbackState {
  message: string;
  type: 'success' | 'error' | 'info' | 'warning';
}

export interface ModalState {
  isOpen: boolean;
  type?: string;
  data?: unknown;
}
